/**
 * Firestore document shapes — the authoritative POPSTAR data model that replaces
 * the legacy client-authoritative RTDB user tree
 * (docs/BARRAGE_IMPLEMENTATION_PLAN.md "Firestore data model";
 * docs/legacy/AVOID_LEGACY_ANALYSIS.md §3). These are PLAIN read shapes — the
 * fields a client may *read*. Server-only fields (xp/level/tokens/ownership) are
 * written exclusively by Cloud Functions (Admin SDK bypasses rules), so a client
 * never trusts or writes them; see the security rules in the plan.
 *
 * Booleans are real booleans here (the legacy app stored `'1'`/`'0'` strings and
 * `0`/`1` ints — normalise at the backend boundary, §5).
 */
import type { Rarity } from '../core/economy';
import type { AttackFamily, GameMode, Viewport } from '../core/types';

/**
 * Account tier (also drives quotas like {@link replayStarQuota}). `free` is the
 * default named tier; `plus`/`pro` are higher tiers granted server-side by an
 * admin (`admin.setTier`). Distinct from the auth-level {@link
 * import('./types').AccountTier} (`anon`/`named`): a `tier` here is always a NAMED
 * account's subscription tier.
 */
export type ProfileTier = 'free' | 'plus' | 'pro';

/**
 * The id of a game in the POPSTAR network. POPSTAR is MULTI-GAME; **Barrage is the
 * first** (and, today, the only) game. This is a STRING UNION on purpose — adding a
 * second game is a one-token extension here (`'barrage' | 'live'`), and every
 * `gameId` parameter across the contract defaults to {@link DEFAULT_GAME_ID} so
 * the single-game callers keep working unchanged (memory: popstar-multigame).
 */
export type GameId = 'barrage';

/**
 * The default game id used EVERYWHERE `gameId` is optional (services facade,
 * Functions contract, security rules). Keeping this constant — rather than a bare
 * literal — means the per-game leaderboard path `leaderboards/{gameId}/scores/{uid}`
 * resolves identically on the writer, the reader, and the rules.
 */
export const DEFAULT_GAME_ID: GameId = 'barrage';

/**
 * The client-side games registry: one entry per {@link GameId}. Mirrors the
 * server `config/games/{gameId}` docs; adding a game = adding ONE entry here plus
 * one to the {@link GameId} union. Used by per-game UI (leaderboard/stats headers).
 */
export const GAMES: Record<GameId, { id: GameId; name: string }> = {
  barrage: { id: 'barrage', name: 'Barrage' },
};

/**
 * `users/{uid}` — NAMED accounts only (anonymous users have no doc at all, so the
 * tier gate is structural, not just rule-based). The non-sensitive fields
 * (`name`, `language`) are self-writable; the economy fields are Function-written.
 */
export interface UserProfile {
  uid: string;
  /** Display name (self-writable). */
  name: string;
  /** UI language code, English-first (self-writable). */
  language: string;
  /** Account creation time (epoch ms). */
  createdAt: number;

  // --- server-authoritative (Functions only; clients read but never write) ---
  /** Lifetime XP. Decompose via {@link import('../core/xp').levelForXp}. */
  xp: number;
  /** Current level (new accounts start at 1). */
  level: number;
  /** Token wallet balance (cosmetics currency). */
  tokens: number;

  /**
   * Subscription tier (Function-written; granted by `admin.setTier`). Drives the
   * replay-star quota — see {@link replayStarQuota}. New accounts start `free`.
   */
  tier: ProfileTier;

  /**
   * The run ids the user has SAVED to their curated replay collection (Function
   * -written via `saveReplay`/`unsaveReplay`). Bounded by {@link
   * savedReplayQuota}. This is the user's private library: `getMyReplays` resolves
   * the run docs whose ids live here. A run must be SAVED (present in this list)
   * before it can be STARRED — {@link starredReplayIds} is a subset of these.
   */
  savedReplayIds: string[];

  /**
   * The run ids the user has STARRED for their public showcase card (Function
   * -written via `setStarredReplay`/`unstarReplay`). Bounded by
   * {@link replayStarQuota}; the most-recently-starred is surfaced on the public
   * {@link UserCard}. Every starred id must also appear in {@link savedReplayIds}
   * (you can only star a SAVED replay).
   */
  starredReplayIds: string[];

  /** Lifetime count of skill stickers earned (score-threshold achievements). */
  skillStickerCount: number;
  /** Lifetime count of seasonal/limited stickers owned. */
  seasonStickerCount: number;
  /** Lifetime count of secret stickers owned. */
  secretStickerCount: number;

  /** The two equip slots (sticker ids, or null when empty). Slot 2 was disabled in legacy. */
  equipped: {
    slot1: string | null;
    slot2: string | null;
  };

  /**
   * PER-GAME personal stats roll-up (Function-written), keyed by {@link GameId}.
   * Holds the survival/death history + unlocked achievements for each game the
   * player has played (see {@link PlayerGameStats}). Optional so profiles written
   * before this field still type-check; readers fall back to
   * {@link emptyPlayerGameStats} for an absent game.
   */
  gameStats?: Partial<Record<GameId, PlayerGameStats>>;
}

/**
 * Replay-star quota by subscription tier: how many runs a user may keep starred
 * on their public showcase card at once. `free` → 1, `plus` → 5, `pro` → 25
 * (owner-mandated defaults). The server enforces this on `setStarredReplay`; the
 * UI uses it to disable the star button at the cap.
 *
 * @param tier the user's {@link ProfileTier}.
 * @returns the maximum number of simultaneously-starred replays.
 */
export function replayStarQuota(tier: ProfileTier): number {
  switch (tier) {
    case 'pro':
      return 25;
    case 'plus':
      return 5;
    case 'free':
    default:
      return 1;
  }
}

/**
 * Saved-replay quota by subscription tier: how many runs a user may keep in their
 * curated SAVED collection at once (the library `getMyReplays` returns). `free` →
 * 3, `plus` → 10, `pro` → 50 (owner-mandated defaults). The server enforces this
 * on `saveReplay`; the UI uses it to disable the "Save replay" button at the cap.
 * A SAVED replay is the prerequisite for STARRING one (see {@link
 * replayStarQuota}), so this quota is always ≥ the star quota for the same tier.
 *
 * @param tier the user's {@link ProfileTier}.
 * @returns the maximum number of simultaneously-saved replays.
 */
export function savedReplayQuota(tier: ProfileTier): number {
  switch (tier) {
    case 'pro':
      return 50;
    case 'plus':
      return 10;
    case 'free':
    default:
      return 3;
  }
}

/**
 * A compact, list-friendly summary of a stored `runs/{runId}` — the shape the UI
 * shows in "my replays" and on a {@link UserCard}, without the full input
 * timeline. Derived from a {@link RunDoc} by the replay/showcase Functions.
 */
export interface RunSummary {
  /** The `runs/{runId}` document id (load the full replay with `getReplay`). */
  id: string;
  /** Authoritative survival score (the only score the server trusts). */
  score: number;
  /** Which attack family landed the kill, or null. */
  deathFamily: AttackFamily | null;
  /** Authoritative survival time (ms). */
  durationMs: number;
  /** When the run was recorded (epoch ms). */
  createdAt: number;
  /** Solo vs multiplayer (multiplayer is a placeholder). */
  mode: GameMode;
}

/**
 * `cards/{uid}` — PUBLIC READ, Function-written. A player's public showcase card:
 * their headline stats, equipped sticker, and the single replay they've chosen to
 * feature (the most-recent of their {@link UserProfile.starredReplayIds}).
 * Surfaced by `getUserCard`. Anyone may read it; only the Functions write it.
 */
export interface UserCard {
  uid: string;
  /** Display name (mirrored from the profile at write time). */
  name: string;
  /** Current level (mirrored from the profile). */
  level: number;
  /** The sticker equipped in slot 1, or null when none. */
  equippedStickerId: string | null;
  /** The featured starred replay, or null when the user has none starred. */
  starred: RunSummary | null;
}

/**
 * `config/runtime` — the live anti-cheat + maintenance toggle (admin-written,
 * public-readable). The `submitRun` handler reads `antiCheatEnabled`: `true` →
 * re-run the deterministic core sim and reject divergent client scores; `false` →
 * trust the client score (the run is still recorded). `maintenance` gates play.
 * Defaults: both decided server-side (anti-cheat defaults ON).
 */
export interface RuntimeConfig {
  /** When true (default), `submitRun` re-simulates and rejects divergent scores. */
  antiCheatEnabled: boolean;
  /** When true, the app shows a maintenance gate instead of letting runs submit. */
  maintenance: boolean;
}

/**
 * Aggregate metrics for the admin dashboard (returned by `admin.getMetrics`).
 * Computed server-side; never client-derived. Read-only snapshot.
 */
export interface AdminMetrics {
  /** Live "players online" presence count. */
  playersOnline: number;
  /** Runs submitted in the current UTC day. */
  runsToday: number;
  /** Total registered (named) users. */
  totalUsers: number;
  /** Total runs ever recorded. */
  totalRuns: number;
  /** Runs flagged by anti-cheat (score divergence) lifetime. */
  flaggedRuns: number;
  /** Sum of all token balances across users (economy health). */
  tokensInCirculation: number;
}

/**
 * Per-attack-family death tally — every {@link AttackFamily} present (0 when the
 * player has never died to it), so the stats donut renders a complete, stable set.
 * The persisted counterpart of the UI's `DeathsByFamily`.
 */
export type DeathsByFamily = Record<AttackFamily, number>;

/** A zeroed {@link DeathsByFamily} (every family at 0). */
export function emptyDeaths(): DeathsByFamily {
  return { walls: 0, lasers: 0, turret: 0, caro: 0, missiles: 0, bombs: 0 };
}

/**
 * Per-game PERSONAL stats aggregate — the server-maintained roll-up of EVERY run
 * a named player has submitted for one game (NOT just their saved replays, which
 * is why the deaths donut can show a complete history). Stored on the profile as
 * `users/{uid}.gameStats[gameId]` (Function-written), so a single `getProfile`
 * read powers the Personal stats tab. The economy (xp/tokens) stays account-wide;
 * THESE numbers are per-game because POPSTAR is multi-game.
 */
export interface PlayerGameStats {
  /** Which game these stats are for. */
  gameId: GameId;
  /** Total validated runs the player has submitted for this game. */
  runs: number;
  /** Sum of every run's score (divide by `runs` for the average). */
  scoreSum: number;
  /** Best single-run score (mirrors the leaderboard entry). */
  bestScore: number;
  /** Longest single survival time (ms). */
  longestMs: number;
  /** Lifetime hazards avoided (approximated from survival time, legacy parity). */
  totalAvoided: number;
  /** How each attack family has killed the player (every family present). */
  deathsByFamily: DeathsByFamily;
  /** The {@link import('../core/achievements').AchievementId}s this player has unlocked. */
  unlockedAchievements: string[];
  /** Last update (epoch ms). */
  updatedAt: number;
}

/** A freshly-zeroed per-game personal stats aggregate. */
export function emptyPlayerGameStats(gameId: GameId = DEFAULT_GAME_ID): PlayerGameStats {
  return {
    gameId,
    runs: 0,
    scoreSum: 0,
    bestScore: 0,
    longestMs: 0,
    totalAvoided: 0,
    deathsByFamily: emptyDeaths(),
    unlockedAchievements: [],
    updatedAt: 0,
  };
}

/**
 * `stats/{gameId}` — PUBLIC READ, Function-written. The NETWORK-WIDE aggregate for
 * one game: how the whole player base is doing, surfaced by `getNetworkStats` on
 * the Stats → Network tab. `recentAvgScore` is the mean over a rolling window of
 * the most-recent runs (so it tracks current form, not lifetime). `achievementCounts`
 * maps each {@link import('../core/achievements').AchievementId} to the number of
 * DISTINCT players who have unlocked it (bumped once, on a player's first unlock).
 */
export interface NetworkStats {
  gameId: GameId;
  /** Total validated runs ever submitted for this game (all players). */
  totalRuns: number;
  /** Best score anyone has posted for this game. */
  bestScore: number;
  /** Mean score over the most-recent {@link NetworkStats.recentWindow} runs. */
  recentAvgScore: number;
  /** How many recent runs `recentAvgScore` is averaged over. */
  recentWindow: number;
  /** Distinct players who have a stats footprint for this game. */
  playersTracked: number;
  /** achievementId → number of distinct players who have unlocked it. */
  achievementCounts: Record<string, number>;
  /** Last update (epoch ms). */
  updatedAt: number;
}

/** A zeroed network-stats snapshot (the read fallback when no doc exists yet). */
export function emptyNetworkStats(gameId: GameId = DEFAULT_GAME_ID): NetworkStats {
  return {
    gameId,
    totalRuns: 0,
    bestScore: 0,
    recentAvgScore: 0,
    recentWindow: 0,
    playersTracked: 0,
    achievementCounts: {},
    updatedAt: 0,
  };
}

/** How many recent run scores the network rolling-average window holds. */
export const NETWORK_RECENT_WINDOW = 200;

/**
 * What a NAMED account earned for a validated run — the XP/level/token/achievement
 * deltas the after-game screen animates. Server-authoritative (computed by the
 * Cloud Function, or the trusted local sim offline); null/absent for anonymous
 * users (who earn nothing) and for a rejected run. Lives here (not just the client
 * facade) so the build's `_shared` copy lets the Function return the same shape.
 */
export interface RunReward {
  /** XP earned this run. */
  xpGained: number;
  /** Lifetime XP BEFORE this run (for the XP-bar start). */
  xpBefore: number;
  /** Lifetime XP AFTER this run (for the XP-bar end). */
  xpAfter: number;
  /** Account level before this run. */
  levelBefore: number;
  /** Account level after this run. */
  levelAfter: number;
  /** True when the run crossed at least one level boundary. */
  leveledUp: boolean;
  /** Tokens earned this run. */
  tokensGained: number;
  /** Achievement ids first unlocked on THIS run (to celebrate). */
  unlockedAchievements: string[];
}

/**
 * `catalog/stickers/{id}` — PUBLIC READ, admin/seed-written. Immutable catalog
 * metadata for one collectible sticker. (Legacy taxonomy was Type→Category→Series
 * →Sticker, §2.5; here it's flattened to a single `id` with descriptive fields.)
 */
export interface StickerCatalogItem {
  id: string;
  title: string;
  desc: string;
  /**
   * Collectible class (how a sticker is EARNED, owner spec 2026-07-07):
   *  - `skill`    — tied to a game's achievements (mastery rewards);
   *  - `seasonal` — event participation only (limited drops);
   *  - `secret`   — everything else, including random low-rarity finds
   *                 (wheel spins etc.). There is NO "regular" class.
   */
  type: 'skill' | 'seasonal' | 'secret';
  /** Theme bucket the sticker belongs to. */
  category: string;
  /** Series within the category. */
  series: string;
  /** Number within the series (legacy s1–s8). */
  num: number;
  /** Rarity tier (drives shop price + wheel odds). */
  rarity: Rarity;
  /** CDN URL of the sticker art (placeholder until the art pipeline lands). */
  imageUrl: string;
}

/**
 * `leaderboards/{gameId}/scores/{uid}` — PUBLIC READ, written ONLY by the validated
 * `submitRun` Function (never client-writable). Each game has its OWN `scores`
 * subcollection, so the top-N query is `orderBy('score','desc').limit(N)` WITHIN it
 * — no composite index needed (memory: popstar-multigame). The exact path is used
 * identically by the Functions writer, the client reader, and the security rules.
 */
export interface LeaderboardEntry {
  uid: string;
  name: string;
  score: number;
  /**
   * Which game this score belongs to. Redundant with the document's path
   * (`leaderboards/{gameId}/scores/{uid}`) so it's OPTIONAL on the read shape and
   * defaults to {@link DEFAULT_GAME_ID} when absent (legacy flat entries predate
   * it); the Function writer stamps it on every new entry.
   */
  gameId?: GameId;
  /** The run that produced this score (links to `runs/{runId}` for replay). */
  runId: string;
  /** Last update (epoch ms). */
  updatedAt: number;
}

/**
 * `runs/{runId}` — the replay payload. Created and locked by `submitRun` after
 * server re-simulation; `validated` is always true once written. Public-readable
 * so POPSTAR LIVE can play it back. Stores the tiny `{seed, inputs}`, not a video.
 */
export interface RunDoc {
  uid: string;
  /**
   * Which game produced this run. `runs/{runId}` stays a single FLAT collection
   * across all games (memory: popstar-multigame); `getMyReplays`/stats filter by
   * this field in code (after the uid-equality query) rather than via a per-game
   * path. OPTIONAL on the read shape so runs written before the multi-game cut-over
   * still type-check; readers treat an absent value as {@link DEFAULT_GAME_ID}, and
   * the Function writer stamps it on every new run.
   */
  gameId?: GameId;
  /** The run seed (re-create the world from this — see {@link import('../core/world').runSimulation}). */
  seed: number;
  /** The player's recorded input timeline (the {@link import('../core/types').RunInputSample}s). */
  inputs: { tMs: number; x: number; y: number }[];
  /** Authoritative score the server computed by re-simulating. */
  score: number;
  /** Authoritative survival time (ms). */
  durationMs: number;
  /** Which attack family landed the kill, or null. */
  deathFamily: AttackFamily | null;
  /** Creation time (epoch ms). */
  createdAt: number;
  /** Always true once written (the server only persists validated runs). */
  validated: true;
  /** Curated for the LIVE feed. */
  featured: boolean;
  /** Play area the run was recorded in — REQUIRED to replay/letterbox it faithfully. */
  viewport: Viewport;
  /** Solo vs multiplayer. */
  mode: GameMode;
  /** Sim version the run was produced under (a replay only re-runs a matching version). */
  simVersion: number;
}

/**
 * `catalog/news/{id}` — PUBLIC READ, admin-written. One news-feed slide.
 */
export interface NewsItem {
  id: string;
  title: string;
  body: string;
  /** CDN URL of the slide image. */
  imageUrl: string;
  /** Language code this item is written in (English-first). */
  lang: string;
  /** Sort order within the feed (ascending). */
  order: number;
  /** Publish time (epoch ms). */
  publishedAt: number;
}

/**
 * `catalog/events/{id}` — PUBLIC READ, admin-written. A timed special event that
 * grants a one-off prize (claim deduplicated server-side, legacy §2.7).
 */
export interface EventDoc {
  id: string;
  /** Whether the event is currently claimable. */
  active: boolean;
  /** What the prize is. */
  prizeType: 'tokens' | 'xp' | 'sticker';
  /** Amount of the prize (tokens/xp) or 1 for a sticker grant. */
  qty: number;
  /** Sticker id when `prizeType === 'sticker'`. */
  stickerId?: string;
  /** Window start (epoch ms). */
  startsAt: number;
  /** Window end (epoch ms). */
  endsAt: number;
}

/**
 * The outcome of claiming a seasonal {@link EventDoc} prize, returned to the client
 * to drive the reveal. The grant has ALREADY been applied server-side and the claim
 * is deduplicated (a second claim of the same event reports `alreadyClaimed`).
 */
export interface EventClaimResult {
  eventId: string;
  /** True when THIS call applied the grant (false when it was already claimed). */
  claimed: boolean;
  /** True when the caller had already claimed this event before. */
  alreadyClaimed: boolean;
  /** What the prize was. */
  prizeType: 'tokens' | 'xp' | 'sticker';
  /** Amount awarded (tokens/xp) or 1 for a sticker. */
  qty: number;
  /** Granted sticker id when a sticker was awarded. */
  stickerId?: string;
  /** Token balance after the claim (server-authoritative), when known. */
  tokensAfter?: number;
}

/**
 * `config/wheel` — PUBLIC READ. A prize-wheel definition. The server uses the
 * slot table + odds to pick a spin outcome (the client never decides the result,
 * unlike legacy §2.7).
 */
export interface WheelConfig {
  /** Wheel identifier (e.g. the daily free wheel vs. the token-trade wheel). */
  wheelId: string;
  /** Token cost to spin (0 for the free daily wheel). */
  cost: number;
  /** Cooldown between spins (ms); the daily wheel uses 24h. */
  cooldownMs: number;
  /** The wheel slots, in display order. */
  slots: WheelSlot[];
}

/** One slot on a {@link WheelConfig}: a prize plus its selection weight. */
export interface WheelSlot {
  /** Position index (1-based, matches the rendered wheel). */
  index: number;
  /** What this slot awards. */
  prizeType: 'tokens' | 'xp' | 'sticker';
  /** Amount (tokens/xp) or 1 for a sticker. */
  qty: number;
  /** Sticker id when `prizeType === 'sticker'`. */
  stickerId?: string;
  /** Relative selection weight (server normalises across slots). */
  weight: number;
}

/**
 * The outcome of a server-resolved wheel spin, returned to the client to drive
 * the reveal animation. The grant has ALREADY been applied server-side.
 */
export interface WheelResult {
  wheelId: string;
  /** The winning slot's position index. */
  slotIndex: number;
  /** What was awarded. */
  prizeType: 'tokens' | 'xp' | 'sticker';
  /** Amount awarded (tokens/xp) or 1 for a sticker. */
  qty: number;
  /** Granted sticker id when a sticker was won. */
  stickerId?: string;
  /** The user's token balance AFTER the spin (server-authoritative). */
  tokensAfter: number;
}
